import ShopType from "@/enum/ShopType";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import WaybillTemplatePrintType from "@/enum/WaybillTemplatePrintType";
import EwaybillType from "@/enum/EwaybillType";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import OrderDTO from "@/dto/order/OrderDTO";
import { hasLocalConsigneeInfo } from "./OrderUtils";
import OrderTag from "@/enum/OrderTag";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { isAnyOrderWithJdConsolidationTag } from "./OrderTagUtils";

/** 特殊订单例如 阿里抖音密文分销 天猫物流订单*/
/** 1.单个店铺打单，先使用规则工具过滤掉不可用的模板，再根据特殊订单进行后置提示 */
/** 2.跨平台打单，根据平台类型先过滤一部分不可用的模板，打印时再使用规则工具进行后置提示，如果有特殊订单也进行后置提示*/
/** 3.扫描打印，所有模板均展示出来，搜索出订单后使用规则工具进行后置提示，如果有特殊订单也进行后置提示 */

/** @see https://qw15p3s8hv.feishu.cn/wiki/YHcPw9YLciBWMbk6ktcc3vKenUd */
export enum WaybillTemplateRule {
    /** 平台与电子面单的规则限制 */
    WEBUY_RULE = "WEBUY_RULE",
    CHANNEL_SHOP_RULE = "CHANNEL_SHOP_RULE",
    CHANNEL_SHOP_DROPSHIP_RULE = "CHANNEL_SHOP_DROPSHIP_RULE",
    TAOBAO_RULE = "TAOBAO_RULE",
    PDD_RULE = "PDD_RULE",
    PDD_FDS_RULE = "PDD_FDS",
    KWAI_RULE = "KWAI_RULE",
    KWAI_DROPSHIP_RULE = "KWAI_DROPSHIP_RULE",
    DOUDIAN_RULE = "DOUDIAN_RULE",
    DOUDIANIOP_RULE = "DOUDIANIOP_RULE",
    XIAOHONGSHU_RULE = "XIAOHONGSHU_RULE",
    JD_PURCHASE_RULE = "JD_PURCHASE_RULE",
    /** 发货易限制 */
    DISABLED_CMD_RULE = "DISABLED_CMD_RULE",
    NOT_ALLOWED_RULE = "NOT_ALLOWED_RULE",
}

/** 校验模板是否符合平台的要求 */
export function checkWaybillTemplatePlatformRule(
    ewaybillAccount: EwaybillAccountDTO,
    shop: ShopDTO,
    template: WaybillTemplateDTO,
): [boolean, WaybillTemplateRule?] {
    /** 京东分销订单模板限制 */
    if (
        shop?.type === ShopType.JD_PURCHASE &&
        ![WaybillTemplateType.JD, WaybillTemplateType.JDL].includes(template.type) &&
        ![LogisticsCompany.JDKY, LogisticsCompany.JD_EXPRESS, LogisticsCompany.JD_HPCP].includes(
            template?.logisticsCompany,
        )
    ) {
        return [false, WaybillTemplateRule.JD_PURCHASE_RULE];
    }

    /** 鲸灵电子面单只能鲸灵使用 */
    if (ewaybillAccount?.type === EwaybillType.WEBUY) {
        if (shop?.type !== ShopType.WEBUY) {
            return [false, WaybillTemplateRule.WEBUY_RULE];
        }
    }

    /** 微信小店供货商只能使用本店开通的微信小店电子面单(与抖店代发一样) */
    if ([ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shop?.type)) {
        if (template.type === WaybillTemplateType.CHANNEL_SHOP) {
            if (
                ewaybillAccount?.type === EwaybillType.CHANNEL_SHOP_DROPSHIP &&
                (shop?.id === ewaybillAccount?.shopId || shop?.ownerShopId === ewaybillAccount?.shopId)
            ) {
                return [true];
            }
        }

        return [false, WaybillTemplateRule.CHANNEL_SHOP_DROPSHIP_RULE];
    }

    /** 微信小店模板限制 */
    if (template.type === WaybillTemplateType.CHANNEL_SHOP) {
        if (
            shop?.type &&
            ![ShopType.CHANNEL_SHOP, ShopType.WEBUY, ShopType.OTHER_DROP_SHIPPING_SUPPLIER].includes(shop?.type)
        ) {
            return [false, WaybillTemplateRule.CHANNEL_SHOP_RULE];
        }

        /** 微信小店供货商只能使用本店开通的微信小店电子面单(与抖店代发一样) */
        if (ewaybillAccount?.type === EwaybillType.CHANNEL_SHOP_DROPSHIP && shop?.id !== ewaybillAccount?.shopId) {
            return [false, WaybillTemplateRule.CHANNEL_SHOP_RULE];
        }
    }

    /** 淘宝模板限制 */
    if ([ShopType.TAOBAO, ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER, ShopType.TAOBAO_DISTRIBUTION].includes(shop?.type)) {
        if (
            ![
                WaybillTemplateType.CAINIAO,
                WaybillTemplateType.TAOBAO_CAINIAO,
                WaybillTemplateType.OFFICIAL,
                WaybillTemplateType.NORMAL,
                WaybillTemplateType.JDL,
            ].includes(template.type)
        ) {
            return [false, WaybillTemplateRule.TAOBAO_RULE];
        }
    }

    /** 拼多多模板限制 */
    if (
        [
            ShopType.PINDUODUO,
            ShopType.PDD_DROP_SHIPPING_SUPPLIER,
            ShopType.PDD_FDS,
            ShopType.PDD_FULFILLMENT,
            ShopType.KTT,
        ].includes(shop?.type)
    ) {
        if (
            [
                WaybillTemplateType.PDD,
                WaybillTemplateType.OFFICIAL,
                WaybillTemplateType.NORMAL,
                WaybillTemplateType.JDL,
            ].includes(template.type)
        ) {
            /** 拼多多官方代发只能使用本店开通的拼多多电子面单 */
            if (shop?.type === ShopType.PDD_FDS) {
                if (ewaybillAccount?.type === EwaybillType.PDD && shop?.ownerShopId === ewaybillAccount?.shopId) {
                    return [true];
                }

                return [false, WaybillTemplateRule.PDD_FDS_RULE];
            }

            return [true];
        } else {
            return [false, WaybillTemplateRule.PDD_RULE];
        }
    }

    /** 快手模板限制 */
    if ([ShopType.KWAI_DROPSHIP, ShopType.KWAI].includes(shop?.type)) {
        if ([WaybillTemplateType.KWAISHOP].includes(template.type)) {
            /** 快手厂家仅支持本店开通的快手电子面单 */
            if (shop?.type === ShopType.KWAI_DROPSHIP) {
                if (ewaybillAccount?.type === EwaybillType.KWAISHOP_DROPSHIP && shop?.id === ewaybillAccount?.shopId) {
                    return [true];
                }

                return [false, WaybillTemplateRule.KWAI_DROPSHIP_RULE];
            }

            return [true];
        } else {
            return [false, WaybillTemplateRule.KWAI_RULE];
        }
    }

    if (
        [
            ShopType.DOUDIAN,
            ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
            ShopType.DOUDIAN_IOP,
            ShopType.DOUDIAN_SUPPLIER,
        ].includes(shop?.type)
    ) {
        if ([WaybillTemplateType.JINRITEMAI].includes(template.type)) {
            /** 抖店厂家代发仅支持本店开通的抖音电子面单 */
            if (shop?.type === ShopType.DOUDIAN_IOP) {
                if (ewaybillAccount?.type === EwaybillType.JINRITEMAI_IOP && shop?.id === ewaybillAccount?.shopId) {
                    return [true];
                }

                return [false, WaybillTemplateRule.DOUDIANIOP_RULE];
            }

            return [true];
        } else {
            return [false, WaybillTemplateRule.DOUDIAN_RULE];
        }
    }

    if ([ShopType.XIAOHONGSHU].includes(shop?.type)) {
        if (![WaybillTemplateType.XIAOHONGSHU].includes(template.type)) {
            return [false, WaybillTemplateRule.XIAOHONGSHU_RULE];
        }
    }

    return [true];
}

export function checkWaybillTemplateEcRule(
    shop: ShopDTO,
    template: WaybillTemplateDTO,
): [boolean, WaybillTemplateRule?] {
    if (!shop || !template) return [true];

    const { id: shopId } = shop;
    const { allowedShopIds } = template;

    if (template.printType === WaybillTemplatePrintType.PRINT_CMD) {
        return [false, WaybillTemplateRule.DISABLED_CMD_RULE];
    }

    /** 如果用户在设置-快递单模板中设置了『可使用该模板的店铺』（WaybillTemplate.allowedShopIds不为空时），只有shopId为允许的店铺的时候才显示模板 */
    if (shopId && allowedShopIds) {
        if (!allowedShopIds.split(",").includes(shopId.toString())) {
            return [false, WaybillTemplateRule.NOT_ALLOWED_RULE];
        }
    }

    return [true];
}

export const allWaybillTypes = Object.values(WaybillTemplateType);

/** 检查模板是否符合平台加密分销订单的规定 */
export function checkOutOrderTemplateRule(orders: OrderDTO[], template: WaybillTemplateDTO, shopType: ShopType) {
    if (shopType === ShopType.ALIBABA) {
        let hasALI_OUT_ORDER_TAOBAO = false;
        let hasALI_OUT_ORDER_PDD = false;
        let hasALI_OUT_ORDER_DY = false;
        let hasALI_OUT_ORDER_KWAI = false;
        let hasAlI_OUT_ORDER_XHS = false;
        let hasAlI_OUT_ORDER_JD = false;
        let hasAlI_OUT_ORDER_WEIXIN = false;

        orders.forEach((order) => {
            if (!order?.tags) return;

            /** 如果存在local地址，则表明是明文信息，不需要限制 */
            if (order.consignee && hasLocalConsigneeInfo(order.consignee)) return;

            order.tags.forEach((tag) => {
                if (tag === OrderTag.ALI_OUT_ORDER_TAOBAO) hasALI_OUT_ORDER_TAOBAO = true;
                if (tag === OrderTag.ALI_OUT_ORDER_PDD) hasALI_OUT_ORDER_PDD = true;
                if (tag === OrderTag.ALI_OUT_ORDER_DY) hasALI_OUT_ORDER_DY = true;
                if (tag === OrderTag.ALI_OUT_ORDER_KWAI) hasALI_OUT_ORDER_KWAI = true;
                if (tag === OrderTag.ALI_OUT_ORDER_XHS) hasAlI_OUT_ORDER_XHS = true;
                if (tag === OrderTag.ALI_OUT_ORDER_JD) hasAlI_OUT_ORDER_JD = true;
                if (tag === OrderTag.ALI_OUT_ORDER_WEIXIN) hasAlI_OUT_ORDER_WEIXIN = true;
            });
        });

        if (
            hasALI_OUT_ORDER_TAOBAO &&
            template.type !== WaybillTemplateType.CAINIAO &&
            template.type !== WaybillTemplateType.TAOBAO_CAINIAO
        ) {
            return "所选订单包含淘宝加密分销订单，需选择淘宝菜鸟电子面单模板进行打印！";
        }

        if (hasALI_OUT_ORDER_PDD && template.type !== WaybillTemplateType.PDD) {
            return "所选订单包含拼多多加密分销订单，需选择拼多多电子面单模板进行打印！";
        }

        if (hasALI_OUT_ORDER_DY && template.type !== WaybillTemplateType.JINRITEMAI) {
            return "所选订单包含抖音加密分销订单，需选择抖音电子面单模板进行打印！";
        }

        if (hasALI_OUT_ORDER_KWAI && template.type !== WaybillTemplateType.KWAISHOP) {
            return "所选订单包含快手加密分销订单，需选择快手电子面单模板进行打印！";
        }

        if (hasAlI_OUT_ORDER_XHS && template.type !== WaybillTemplateType.XIAOHONGSHU) {
            return "所选订单包含小红书加密分销订单，需选择小红书电子面单模板进行打印！";
        }

        if (
            hasAlI_OUT_ORDER_JD &&
            ![WaybillTemplateType.JD, WaybillTemplateType.JDL].includes(template.type) &&
            !(
                template.type === WaybillTemplateType.OFFICIAL &&
                [LogisticsCompany.JDKY, LogisticsCompany.JD_EXPRESS, LogisticsCompany.JD_HPCP].includes(
                    template.logisticsCompany,
                )
            )
        ) {
            return "所选订单包含京东加密分销订单，需选择京东无界或京东物流官方模板进行打印！";
        }

        if (hasAlI_OUT_ORDER_WEIXIN && template.type !== WaybillTemplateType.CHANNEL_SHOP) {
            return "所选订单包含微信小店加密分销订单，需选择微信小店电子面单模板进行打印！";
        }
    } else if (shopType === ShopType.WEBUY) {
        const isChannelShopTemplate = template.type === WaybillTemplateType.CHANNEL_SHOP;
        let hasChannelShopTagOrder = false;
        let hasWithoutChannelShopTagOrder = false;

        orders.forEach((order) => {
            if (order.tags?.includes(OrderTag.JL_OUT_ORDER_CHANNELSHOP)) {
                hasChannelShopTagOrder = true;
            } else {
                hasWithoutChannelShopTagOrder = true;
            }
        });

        if (hasChannelShopTagOrder && !isChannelShopTemplate) {
            return "所选订单中有微信小店加密分销订单，需选择微信小店电子面单模板进行打印！";
        } else if (!hasChannelShopTagOrder && isChannelShopTemplate) {
            return "微信小店电子面单不支持非微信小店加密订单打印";
        } else if (hasWithoutChannelShopTagOrder && isChannelShopTemplate) {
            return "所选订单中有非微信小店加密订单，微信小店电子面单不支持非微信小店加密订单打印！";
        }
    } else if (shopType === ShopType.JD) {
        let hasJD_ENCRYPTED_OUT_ORDER_DOUDIAN = false;
        let hasJD_ENCRYPTED_OUT_ORDER_KWAI = false;

        orders.forEach((order) => {
            if (!order?.tags) return;

            order.tags.forEach((tag) => {
                if (tag === OrderTag.JD_ENCRYPTED_OUT_ORDER_DOUDIAN) hasJD_ENCRYPTED_OUT_ORDER_DOUDIAN = true;
                if (tag === OrderTag.JD_ENCRYPTED_OUT_ORDER_KWAI) hasJD_ENCRYPTED_OUT_ORDER_KWAI = true;
            });
        });

        if (hasJD_ENCRYPTED_OUT_ORDER_DOUDIAN && template.type !== WaybillTemplateType.JINRITEMAI) {
            return "所选订单包含云交易抖店加密订单，需选择抖店电子面单模板进行打印！";
        }

        if (hasJD_ENCRYPTED_OUT_ORDER_KWAI && template.type !== WaybillTemplateType.KWAISHOP) {
            return "所选订单包含云交易快手加密订单，需选择快手电子面单模板进行打印！";
        }
    }

    return "";
}

/** 检查模板是否符合承诺日达订单的规定 */
export function checkAppointmentArrivalOrderTemplateRule(orders: OrderDTO[], template: WaybillTemplateDTO) {
    if (![LogisticsCompany.SF, LogisticsCompany.JD_EXPRESS].includes(template?.logisticsCompany)) {
        const hasDouDianAppointmentArrivalOrder = orders.some((order) =>
            order.tags?.some((tag) => [OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL].includes(tag)),
        );

        return hasDouDianAppointmentArrivalOrder
            ? "抖店平台要求承诺日达订单必须使用京东快递或者顺丰快递进行打印发货"
            : undefined;
    }

    return undefined;
}

/** 检查模板是否符合发顺丰订单的规定 */
export function checkShipBySfOrderTemplateRule(orders: OrderDTO[], template: WaybillTemplateDTO) {
    if (template.logisticsCompany !== LogisticsCompany.SF) {
        const hasShipBySFOrder = orders.some((order) =>
            order.tags?.some((tag) =>
                [
                    OrderTag.PDD_SHIP_BY_SF,
                    OrderTag.DOUDIAN_SHIP_BY_SF,
                    OrderTag.FREE_SF,
                    OrderTag.TAOTIAN_SHIP_BY_SF,
                    OrderTag.KWAI_SHIP_BY_SF,

                    OrderTag.SHIP_BY_SF,
                    OrderTag.SHIP_BY_SF_EXPRESS,
                    OrderTag.SHIP_BY_SF_EXPRESS_TK,
                    OrderTag.DOUDIAN_PAID_SF_EXPRESS,
                    OrderTag.DOUDIAN_PAID_SF_EXPRESS_TK,
                ].includes(tag),
            ),
        );

        return hasShipBySFOrder
            ? "选择的订单有“发顺丰”的订单，只支持打印顺丰的模板，请更换模板打印或者重新选择订单打印"
            : "";
    }

    return "";
}

export function checkEncryptedManualOrderTemplateRule(
    orders: OrderDTO[],
    template: WaybillTemplateDTO,
    ewaybillAccount: EwaybillAccountDTO,
) {
    let hasDyEncryptedOrder = false;
    let hasPddEncryptedOrder = false;
    let hasKwaiEncryptedOrder = false;
    let hasTbEncryptedOrder = false;
    let hasJdEncryptedOrder = false;
    let hasXhsEncryptedOrder = false;

    orders.forEach((order) => {
        order.tags?.forEach((orderTag) => {
            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_DOUDIAN) {
                hasDyEncryptedOrder = true;
            }

            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_PDD) {
                hasPddEncryptedOrder = true;
            }

            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_KWAI) {
                hasKwaiEncryptedOrder = true;
            }

            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_TAOBAO) {
                hasTbEncryptedOrder = true;
            }

            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_JD) {
                hasJdEncryptedOrder = true;
            }

            if (orderTag === OrderTag.ENCRYPTED_MANUAL_ORDER_XHS) {
                hasXhsEncryptedOrder = true;
            }
        });
    });

    if (hasDyEncryptedOrder) {
        /** 这里不能直接使用模板类型判断，因为抖店有在线寄件的电子面单类型，也是JINRITEMAI的模板类型 */
        if (
            ![
                EwaybillType.JINRITEMAI,
                EwaybillType.JINRITEMAI_AUTH,
                EwaybillType.JINRITEMAI_AUTH_SHARE,
                EwaybillType.JINRITEMAI_SHARE,
                EwaybillType.JINRITEMAI_SHOP_AUTH,
                EwaybillType.JINRITEMAI_IOP,
            ].includes(ewaybillAccount?.type)
        ) {
            return "所选订单包含抖音加密订单，需选择抖音电子面单模板进行打印";
        }
    }

    if (hasPddEncryptedOrder && WaybillTemplateType.PDD !== template?.type) {
        return "所选订单包含拼多多加密订单，需选择拼多多电子面单模板进行打印";
    }

    if (hasKwaiEncryptedOrder && WaybillTemplateType.KWAISHOP !== template?.type) {
        return "所选订单包含快手加密订单，需选择快手电子面单模板进行打印";
    }

    if (
        hasTbEncryptedOrder &&
        ![WaybillTemplateType.CAINIAO, WaybillTemplateType.TAOBAO_CAINIAO].includes(template?.type)
    ) {
        return "所选订单包含淘宝加密订单，需选择菜鸟电子面单模板进行打印";
    }

    if (hasJdEncryptedOrder) {
        if ([WaybillTemplateType.JDL, WaybillTemplateType.JD].includes(template?.type)) {
            // 允许打印
        } else if (
            [WaybillTemplateType.OFFICIAL].includes(template?.type) &&
            [LogisticsCompany.JDKY, LogisticsCompany.JD_EXPRESS, LogisticsCompany.JD_HPCP].includes(
                template.logisticsCompany,
            )
        ) {
            // 允许打印
        } else {
            return "所选订单包含京东加密订单，需选择京东电子面单模板进行打印";
        }
    }

    if (hasXhsEncryptedOrder && ![WaybillTemplateType.XIAOHONGSHU].includes(template?.type)) {
        return "所选订单包含小红书加密订单，需选择小红书电子面单模板进行打印";
    }

    return "";
}

export function checkJdConsolidationOrderTemplateRule(orders: OrderDTO[], template: WaybillTemplateDTO) {
    const hasJdConsolidationOrder = orders.some(isAnyOrderWithJdConsolidationTag);

    if (hasJdConsolidationOrder) {
        if (template.type !== WaybillTemplateType.JD && template.logisticsCompany !== LogisticsCompany.JD_EXPRESS) {
            return "所选京东新疆集运订单仅支持京东无界电子面单和京东快递打印，请重新选择模板打印";
        }
    }

    return "";
}
